import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import tailwindClassnames from "../common/TailwindClassnames";

type ConfidenceRangeInputProps = {
  /** Current lower bound (0–1), or null when unset. */
  min: number | null;
  /** Current upper bound (0–1), or null when unset. */
  max: number | null;
  /** Called with the new (min, max) pair whenever either handle changes. */
  onChange: (min: number, max: number) => void;
  /** Inline validation message, if any. */
  error?: string;
};

const toPctInt = (v: number) => Math.round(v * 100);
const fromPctInt = (v: number) => v / 100;

/**
 * Shared thumb styling so both overlaid range inputs render one visual handle
 * each on the same track. Also forces both browsers' native track/progress
 * pseudo-elements transparent — `appearance-none` on the input alone doesn't
 * reliably suppress Chrome's `::-webkit-slider-runnable-track` or Firefox's
 * `::-moz-range-track`/`::-moz-range-progress`, and their default light-grey
 * fill was painting over the custom track/active-segment divs underneath in
 * light mode, making the whole control look invisible. The thumb is a white
 * fill with a sky-blue border in both themes, per design — note the explicit
 * `border-solid`: a pseudo-element's own initial `border-style` is `none`
 * regardless of the outer `<input>`'s preflight reset (border-style isn't
 * CSS-inherited, and preflight's universal reset only targets `*`/`::before`/
 * `::after`, not `::-webkit-slider-thumb`), so border-width/border-color
 * alone render nothing without it.
 */
const thumbClassnames =
  "[&::-webkit-slider-runnable-track]:bg-transparent " +
  "[&::-moz-range-track]:bg-transparent [&::-moz-range-progress]:bg-transparent " +
  "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 " +
  "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-solid " +
  "[&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-sky-blue " +
  "[&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:cursor-pointer " +
  "[&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 " +
  "[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-solid " +
  "[&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-sky-blue " +
  "[&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:cursor-pointer";

/**
 * Dual-thumb 0–1 confidence range control for the AI-confidence filter, with
 * numeric percent readouts. Values are the top-candidate score.
 *
 * The two handles share one visual track: two native `<input type="range">`
 * elements (0–100 integer percent scale internally, to avoid float rounding)
 * are absolutely stacked on top of a single track element, each with
 * `pointer-events-none` on everything but its own thumb (via the
 * `::-webkit-slider-thumb`/`::-moz-range-thumb` pseudo-elements), so the two
 * thumbs behave as one slider rather than two separate bars. Both handles are
 * cross-clamped so neither can be dragged/typed past the other.
 *
 * The numeric inputs keep their own local "draft" text so the user can freely
 * clear/retype digits (e.g. clearing "0" to type "40") without React
 * snapping the field back to the last committed value mid-edit — the draft
 * only reconciles with the committed value on blur.
 */
const ConfidenceRangeInput: React.FC<ConfidenceRangeInputProps> = ({
  min,
  max,
  onChange,
  error,
}) => {
  const { t } = useTranslation();
  const minVal = min ?? 0;
  const maxVal = max ?? 1;
  const minPct = toPctInt(minVal);
  const maxPct = toPctInt(maxVal);

  const clampPct = (v: number) => Math.min(100, Math.max(0, v));
  const handleMinPctChange = (next: number) => {
    onChange(fromPctInt(Math.min(clampPct(next), maxPct)), maxVal);
  };
  const handleMaxPctChange = (next: number) => {
    onChange(minVal, fromPctInt(Math.max(clampPct(next), minPct)));
  };

  const [minText, setMinText] = useState(String(minPct));
  const [maxText, setMaxText] = useState(String(maxPct));
  const minFocused = useRef(false);
  const maxFocused = useRef(false);

  useEffect(() => {
    if (!minFocused.current) setMinText(String(minPct));
  }, [minPct]);
  useEffect(() => {
    if (!maxFocused.current) setMaxText(String(maxPct));
  }, [maxPct]);

  return (
    <div className="flex flex-col">
      <div className="flex flex-row justify-between">
        <label
          htmlFor="confidence-min-input"
          className={tailwindClassnames.textInputLabel}
        >
          {t("AI Confidence")}
        </label>
        <span className="text-xs text-off-black-light dark:text-primary-off-white">
          {minPct}% — {maxPct}%
        </span>
      </div>
      <div className="relative h-4 flex items-center">
        <div className="absolute left-0 right-0 h-1 rounded-full bg-off-white-dark dark:bg-storm-blue-border" />
        <div
          className="absolute h-1 rounded-full bg-sky-blue"
          style={{ left: `${minPct}%`, right: `${100 - maxPct}%` }}
        />
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={minPct}
          aria-label={t("Minimum confidence")}
          className={`absolute left-0 right-0 w-full h-4 appearance-none bg-transparent pointer-events-none cursor-pointer z-10 ${thumbClassnames}`}
          onChange={(e) => handleMinPctChange(parseInt(e.target.value, 10))}
          data-testid="confidence-min-slider"
        />
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={maxPct}
          aria-label={t("Maximum confidence")}
          className={`absolute left-0 right-0 w-full h-4 appearance-none bg-transparent pointer-events-none cursor-pointer z-20 ${thumbClassnames}`}
          onChange={(e) => handleMaxPctChange(parseInt(e.target.value, 10))}
          data-testid="confidence-max-slider"
        />
      </div>
      <div className="flex flex-row items-center gap-2 mt-2">
        <input
          id="confidence-min-input"
          type="number"
          min={0}
          max={100}
          step={1}
          value={minText}
          aria-label={t("Minimum confidence percent")}
          className="w-16 rounded px-2 py-1 text-sm text-primary-off-black dark:text-off-white-dark bg-white dark:bg-storm-blue border border-off-white-dark dark:border-off-black-light transition duration-300"
          onFocus={() => (minFocused.current = true)}
          onChange={(e) => {
            setMinText(e.target.value);
            const parsed = parseInt(e.target.value, 10);
            if (!isNaN(parsed)) handleMinPctChange(parsed);
          }}
          onBlur={() => {
            minFocused.current = false;
            setMinText(String(toPctInt(minVal)));
          }}
          data-testid="confidence-min-input"
        />
        <span className="text-xs text-off-black-light dark:text-greyed-text">
          {t("to")}
        </span>
        <input
          type="number"
          min={0}
          max={100}
          step={1}
          value={maxText}
          aria-label={t("Maximum confidence percent")}
          className="w-16 rounded px-2 py-1 text-sm text-primary-off-black dark:text-off-white-dark bg-white dark:bg-storm-blue border border-off-white-dark dark:border-off-black-light transition duration-300"
          onFocus={() => (maxFocused.current = true)}
          onChange={(e) => {
            setMaxText(e.target.value);
            const parsed = parseInt(e.target.value, 10);
            if (!isNaN(parsed)) handleMaxPctChange(parsed);
          }}
          onBlur={() => {
            maxFocused.current = false;
            setMaxText(String(toPctInt(maxVal)));
          }}
          data-testid="confidence-max-input"
        />
      </div>
      {error && (
        <p className="bg-primary-orange-light rounded text-primary-orange-dark text-sm px-3 py-2 my-1">
          {t(error)}
        </p>
      )}
    </div>
  );
};

export default ConfidenceRangeInput;
